<template>
  <fragment>
    <div class="cart-drawer-upsell-container">
      <div class="cart-drawer-upsell-title-container" v-if="upsells.length">
        <h3 class="cart-drawer-upsell-header" v-html="header"></h3>
        <p class="cart-drawer-upsell-subheader" v-html="subheader"></p>
      </div>
      <transition-group class="cart-drawer-upsells" name="list" tag="ul">
        <cart-drawer-upsell-item
          v-for="(upsell, index) in upsells"
          :key="upsell.key"
          :upsell="upsell"
          :index="index"
          :productTitle="upsellProductTitles[index]"
        >
        </cart-drawer-upsell-item>
      </transition-group>

      <div
        v-show="giftWithPurchase.length"
        class="cart-gift-with-purchase-container"
        ref="giftWithPurchaseContainer"
      >
        <h3
          class="cart-drawer-gift-with-purchase-header"
          v-html="giftWithPurchaseHeader"
        ></h3>

        <transition-group class="cart-gift-with-purchase" name="list">
          <cart-drawer-upsell-item
            v-for="(upsell, index) in giftWithPurchase"
            :key="upsell.key"
            :upsell="upsell"
            :index="index"
          >
          </cart-drawer-upsell-item>
        </transition-group>
      </div>
    </div>
    <portal to="cart-bottom" v-if="giftWithPurchase.length">
      <transition name="fade">
        <button
          tabindex="0"
          @click="scrollToGwp"
          aria-label="scroll to gift with purchase"
          class="cart-gwp-alert"
          v-show="gwpVisible === false"
          v-html="gwpAlert"
        ></button>
      </transition>
    </portal>
  </fragment>
</template>

<script>
import { mapGetters, mapState } from "vuex";
export default {
  name: "cart-drawer-upsells",
  props: {
    gwpAlert: String,
  },
  data() {
    return {
      gwpVisible: false,
      gwpObserver: null,
      cartOpenedOnce: false,
    };
  },
  computed: {
    ...mapState({
      cart: (state) => state.cart.data,
      open: (state) => state.cart.open,
      header: (state) => state.metafields.globals.upsell_header,
      subheader: (state) => state.metafields.globals.upsell_subheader,
      giftWithPurchaseHeader: (state) =>
        state.metafields.globals.gift_with_purchase_header,
    }),
    ...mapGetters({
      activeUpsells: "metafields/activeUpsells",
      upsells: "metafields/upsells",
      giftWithPurchase: "metafields/giftWithPurchase",
      getVariantsFromProductUpsells: "metafields/getVariantsFromProductUpsells",
      getUpsellPrices: "metafields/getUpsellPrices",
      getProductTitle: "metafields/getProductTitle",
      optionsScoreMap: "cart/optionsScoreMap",
    }),
    upsellVariants() {
      const productVariants = this.upsells.map(
        (upsell) =>
          this.getVariantsFromProductUpsells(upsell, this.optionsScoreMap) || []
      );
      const variants = this.upsells.map(
        (upsell) =>
          upsell.variants.map((upsellVariant) => upsellVariant.variant) || []
      );
      const upsellVariants = productVariants.map((productVariant, index) =>
        productVariant.concat(variants[index])
      );
      return upsellVariants;
    },
    upsellProductTitles() {
      return this.upsells.map((upsell) => {
        const productTitles = upsell.products.map((product) => product.title);
        const variantProductTitles = upsell.variants.map((upsellVariant) =>
          this.getProductTitle(upsellVariant.product_id)
        );
        return productTitles.concat(variantProductTitles).join("|");
      });
    },
    upsellVariantIds() {
      return this.upsellVariants.map((variants) =>
        variants.map((variant) => variant.id).join("|")
      );
    },
    upsellVariantTitles() {
      return this.upsellVariants.map((variants) =>
        variants.map((variant) => variant.name).join("|")
      );
    },
    upsellVariantSkus() {
      return this.upsellVariants.map((variants) =>
        variants.map((variant) => variant.sku).join("|")
      );
    },
    upsellPrices() {
      return this.upsells.map((upsell) => {
        const { price } = this.getUpsellPrices(upsell);
        return price ? (price / 100).toFixed(2) : null;
      });
    },
  },
  methods: {
    updateGwpVisibility(isVisible) {
      this.gwpVisible = isVisible;
    },
    scrollToGwp(e) {
      e.preventDefault();
      const gwpContainer = this.$refs.giftWithPurchaseContainer;
      gwpContainer.scrollIntoView({ behavior: "smooth" });
    },
    handleViewUpsellEvent() {
      if (!window.maestro) return;
      const user = theme.customer;
      const eventData = {
        "cart token": this.cart.token,
        "items in cart - product titles": this.cart.items.map(
          (item) => item.product_title
        ),
        "items in cart - variant titles": this.cart.items.map(
          (item) => item.variant_title
        ),
        "items in cart - variant ids": this.cart.items.map(
          (item) => item.variant_id
        ),
        "items in cart - prices": this.cart.items.map((item) =>
          (item.final_line_price / 100).toFixed(2)
        ),
        "items in cart - skus": this.cart.items.map((item) => item.sku),
        "upsells shown - product titles": this.upsellProductTitles,
        "upsells shown - variant titles": this.upsellVariantTitles,
        "upsells shown - variant ids": this.upsellVariantIds,
        "upsells shown - prices": this.upsellPrices,
        "upsells shown - skus": this.upsellVariantSkus,
        "customer - logged in": user.isLoggedIn,
        "customer - shopify id": user.id,
        "customer - email": user.email ? user.email : "",
        "customer - total orders":
          user.orders_count || user.orders_count == 0 ? user.orders_count : "",
        "customer - total spent":
          user.total_spent || user.total_spent == 0 ? user.total_spent : "",
      };

      window.maestro.fireTrackEvent("viewCartUpsells", eventData);
    },
    isEqualUpsells(newUpsells, prevUpsells) {
      if (newUpsells.length !== prevUpsells.length) {
        return false;
      }

      let isEqual = true;
      newUpsells.forEach((upsell, index) => {
        const prevUpsell = prevUpsells[index];
        if (upsell.key !== prevUpsell.key) isEqual = false;
      });
      return isEqual;
    },
  },
  mounted() {
    this.gwpObserver = new IntersectionObserver(
      (entry) => {
        const rootEntry = entry[0];
        if (!rootEntry) return false;
        const isVisible = this.open ? rootEntry.isIntersecting : null;
        this.updateGwpVisibility(isVisible);
      },
      { root: null, threshold: 0.1 }
    );

    const gwpContainer = this.$refs.giftWithPurchaseContainer;
    if (gwpContainer) {
      this.gwpObserver.observe(this.$refs.giftWithPurchaseContainer);
    }
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        const gwpContainer = this.$refs.giftWithPurchaseContainer;
        if (gwpContainer) {
          this.gwpObserver.unobserve(gwpContainer);
          this.gwpObserver.observe(gwpContainer);
        }

        if (!this.cartOpenedOnce) {
          if (this.upsells.length) {
            this.handleViewUpsellEvent();
          }

          this.cartOpenedOnce = true;
        }
      }
    },
    activeUpsells: {
      handler(newUpsells, prevUpsells) {
        if (!this.cartOpenedOnce || !newUpsells.length) return;
        if (!this.isEqualUpsells(newUpsells, prevUpsells)) {
          this.handleViewUpsellEvent();
        }
      },
      immediate: true,
    },
  },
};
</script>
